import { Roles } from '@omnea-digital/toolbox'
import { BlockTypeEnum } from '@omnea-app/core/blocks'
import { RequestStepAction } from '@omnea-app/core/stages/utilEnums'
import { RequestStepResultEnum, RequestStepStateEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { BlockContext } from './types'

type ActionStepStates = RequestStepStateEnum | RequestStepResultEnum

const AllowedActions = [
  RequestStepAction.FORCE_APPROVE,
  RequestStepAction.REASSIGN_TO_SELF,
  RequestStepAction.HTTP_RETRY,
  RequestStepAction.REASSIGN_APPROVERS,
  RequestStepAction.ROLLBACK,
  RequestStepAction.SKIP,
  RequestStepAction.RERUN_ACCOUNTING_SYNC,
  RequestStepAction.UN_SKIP,
  RequestStepAction.POLL,
  RequestStepAction.REFRESH_STATUS,
  RequestStepAction.UPDATE_CONFIG,
  RequestStepAction.DATA_TRANSFORMATION_RETRY,
  RequestStepAction.SKIP_CONDITIONS,
] as const satisfies RequestStepAction[]
type AllowedAction = (typeof AllowedActions)[number]

const PausedActions = [
  RequestStepAction.REASSIGN_APPROVERS,
  RequestStepAction.REASSIGN_SUPPLIER,
  RequestStepAction.REASSIGN_ENGAGEMENT,
  RequestStepAction.REASSIGN_TO_SELF,
  RequestStepAction.REASSIGN_RENEWAL_ASSESSMENT,
  RequestStepAction.REASSIGN_EXTERNAL_OPERATION,
  RequestStepAction.REASSIGN_PO_ASSIGNEES,
]

export const skippableBlockTypes = [
  BlockTypeEnum.APPROVAL,
  BlockTypeEnum.SUPPLIER_ONBOARDING,
  BlockTypeEnum.CREATE_RENEWAL,
  BlockTypeEnum.COMMIT_SUPPLIER_DATA,
  BlockTypeEnum.CREATE_PO,
  BlockTypeEnum.RFX,
  BlockTypeEnum.CREATE_AGREEMENT,
  BlockTypeEnum.CANCEL_AGREEMENT,
  BlockTypeEnum.DATA_TRANSFORMATION,
  BlockTypeEnum.PRICE_BENCHMARK,
  BlockTypeEnum.EXTERNAL_OPERATION,
]

/** Block types that can be skipped when in a future (PENDING) state. Includes NOTIFICATION which only supports skip when pending, not running. */
export const futureSkippableBlockTypes = [...skippableBlockTypes, BlockTypeEnum.NOTIFICATION]

const rollbackableBlockTypes = [
  BlockTypeEnum.BUDGET_APPROVAL,
  BlockTypeEnum.APPROVAL,
  BlockTypeEnum.SUPPLIER_ONBOARDING,
  BlockTypeEnum.ENGAGEMENT,
  BlockTypeEnum.CREATE_RENEWAL,
  BlockTypeEnum.COMMIT_SUPPLIER_DATA,
  BlockTypeEnum.NOTIFICATION,
  BlockTypeEnum.CREATE_PO,
  BlockTypeEnum.CREATE_AGREEMENT,
  BlockTypeEnum.CREATE_ASSESSMENT,
  BlockTypeEnum.INTAKE,
  BlockTypeEnum.EXTERNAL_OPERATION,
  BlockTypeEnum.RENEWAL_ASSESSMENT,
  BlockTypeEnum.HTTP,
  BlockTypeEnum.MILESTONE,
]

export type FlagId = 'isAssignee'

interface PermissionRule {
  states: ActionStepStates[]
  role: Roles
  types: BlockTypeEnum[]
  // Only allow for the most recent stage of a request, if complete this is the final stage
  shouldOnlyApplyToMostRecentStage?: boolean
  omneanOnly?: boolean
  needsConfigPermission?: boolean
  mustNotBeViewer?: boolean
  mustBeAssignee?: boolean
  requiresAccountingIntegration?: boolean
  requiresTaskErrors?: boolean
  requiresAllUserTasksComplete?: boolean
  requiresStepNotLoading?: boolean
  checkFeatureFlag?: boolean
  killedByAssigneeRestriction?: boolean
  requiresOpenAmendment?: boolean
  requiresRequestInFlight?: boolean
  requiresConditionality?: boolean
}

type Permissions = Record<AllowedAction, PermissionRule[]>

const baseReassignmentPermissionRules: PermissionRule[] = [
  // Note(Diego): In the future we will remove result: Error for updates and replace this with Stage.insertTaskError. More info at: https://github.com/omnea-digital/omnea-app/pull/10345
  {
    states: [RequestStepStateEnum.RUNNING, RequestStepStateEnum.PENDING, RequestStepStateEnum.PAUSED],
    types: [
      BlockTypeEnum.APPROVAL,
      BlockTypeEnum.SUPPLIER_ONBOARDING,
      BlockTypeEnum.RENEWAL_ASSESSMENT,
      BlockTypeEnum.ENGAGEMENT,
      BlockTypeEnum.CREATE_RENEWAL,
      BlockTypeEnum.COMMIT_SUPPLIER_DATA,
      BlockTypeEnum.CREATE_PO,
      BlockTypeEnum.EXTERNAL_OPERATION, // TODO EXP-1373 only if there is a task.
      BlockTypeEnum.CREATE_AGREEMENT,
      BlockTypeEnum.CANCEL_AGREEMENT,
      BlockTypeEnum.CREATE_ASSESSMENT,
      BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES,
    ],
    role: Roles.ADMIN,
    mustNotBeViewer: true,
  },
  {
    states: [RequestStepStateEnum.RUNNING],
    types: [BlockTypeEnum.AI_FORM_COMPLETION],
    role: Roles.ADMIN,
    mustNotBeViewer: true,
  },
  {
    states: [RequestStepStateEnum.RUNNING, RequestStepStateEnum.PAUSED],
    types: [BlockTypeEnum.BUDGET_APPROVAL],
    role: Roles.ADMIN,
    mustNotBeViewer: true,
  },
  {
    states: [RequestStepStateEnum.RUNNING, RequestStepStateEnum.PENDING, RequestStepStateEnum.PAUSED],
    types: [
      BlockTypeEnum.APPROVAL,
      BlockTypeEnum.SUPPLIER_ONBOARDING,
      BlockTypeEnum.RENEWAL_ASSESSMENT,
      BlockTypeEnum.ENGAGEMENT,
      BlockTypeEnum.CREATE_RENEWAL,
      BlockTypeEnum.COMMIT_SUPPLIER_DATA,
      BlockTypeEnum.CREATE_PO,
      BlockTypeEnum.EXTERNAL_OPERATION, // TODO EXP-1373 only if there is a task.
      BlockTypeEnum.CREATE_AGREEMENT,
      BlockTypeEnum.CANCEL_AGREEMENT,
      BlockTypeEnum.CREATE_ASSESSMENT,
      BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES,
    ],
    role: Roles.ADMIN,
    mustNotBeViewer: false,
    mustBeAssignee: true,
  },
  {
    states: [RequestStepStateEnum.RUNNING],
    types: [BlockTypeEnum.AI_FORM_COMPLETION],
    role: Roles.ADMIN,
    mustNotBeViewer: false,
    mustBeAssignee: true,
  },
  {
    states: [RequestStepStateEnum.RUNNING, RequestStepStateEnum.PAUSED],
    types: [BlockTypeEnum.BUDGET_APPROVAL],
    role: Roles.ADMIN,
    mustNotBeViewer: false,
    mustBeAssignee: true,
  },
  {
    states: [RequestStepStateEnum.RUNNING, RequestStepStateEnum.PENDING, RequestStepStateEnum.PAUSED],
    types: [
      BlockTypeEnum.APPROVAL,
      BlockTypeEnum.SUPPLIER_ONBOARDING,
      BlockTypeEnum.RENEWAL_ASSESSMENT,
      BlockTypeEnum.ENGAGEMENT,
    ],
    role: Roles.USER,
    mustBeAssignee: true,
    killedByAssigneeRestriction: true,
  },
  {
    states: [RequestStepStateEnum.RUNNING, RequestStepStateEnum.PAUSED],
    types: [BlockTypeEnum.BUDGET_APPROVAL],
    role: Roles.USER,
    mustBeAssignee: true,
    killedByAssigneeRestriction: true,
  },
]

// An outstanding amendment effectively reopens a COMPLETE step.
const amendmentReopenedReassignmentRules: PermissionRule[] = baseReassignmentPermissionRules.map((rule) => ({
  ...rule,
  states: [RequestStepStateEnum.COMPLETE],
  requiresOpenAmendment: true,
}))

// A completed SUPPLIER_ONBOARDING step on a still-active request stays editable
// so admins can add new collaborators to the supplier portal.
const inFlightSupplierPortalReassignmentRules: PermissionRule[] = [
  {
    states: [RequestStepStateEnum.COMPLETE],
    types: [BlockTypeEnum.SUPPLIER_ONBOARDING],
    role: Roles.ADMIN,
    mustNotBeViewer: true,
    requiresRequestInFlight: true,
  },
  {
    states: [RequestStepStateEnum.COMPLETE],
    types: [BlockTypeEnum.SUPPLIER_ONBOARDING],
    role: Roles.ADMIN,
    mustBeAssignee: true,
    requiresRequestInFlight: true,
  },
  {
    states: [RequestStepStateEnum.COMPLETE],
    types: [BlockTypeEnum.SUPPLIER_ONBOARDING],
    role: Roles.USER,
    mustBeAssignee: true,
    killedByAssigneeRestriction: true,
    requiresRequestInFlight: true,
  },
]

const reassignmentPermissionRules: PermissionRule[] = [
  ...baseReassignmentPermissionRules,
  ...amendmentReopenedReassignmentRules,
  ...inFlightSupplierPortalReassignmentRules,
]

const permissions: Permissions = {
  [RequestStepAction.REASSIGN_APPROVERS]: reassignmentPermissionRules,
  [RequestStepAction.REASSIGN_TO_SELF]: reassignmentPermissionRules,

  [RequestStepAction.FORCE_APPROVE]: [
    {
      states: [RequestStepStateEnum.RUNNING],
      types: [BlockTypeEnum.BUDGET_APPROVAL, BlockTypeEnum.APPROVAL],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
    },
  ],
  [RequestStepAction.SKIP]: [
    {
      states: [RequestStepStateEnum.RUNNING, RequestStepStateEnum.PENDING],
      types: skippableBlockTypes,
      role: Roles.ADMIN,
      mustNotBeViewer: true,
    },
    {
      states: [RequestStepStateEnum.PENDING],
      types: [BlockTypeEnum.NOTIFICATION],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
    },
    {
      states: [RequestStepStateEnum.RUNNING],
      types: [
        BlockTypeEnum.BUDGET_APPROVAL,
        BlockTypeEnum.EXTERNAL_OPERATION,
        BlockTypeEnum.CREATE_ASSESSMENT,
        BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES,
      ],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
    },
    {
      states: [RequestStepResultEnum.ERROR],
      types: [BlockTypeEnum.HTTP, BlockTypeEnum.EXTERNAL_OPERATION, BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
    },
    {
      states: [RequestStepStateEnum.RUNNING],
      types: [BlockTypeEnum.ENGAGEMENT],
      omneanOnly: true,
      role: Roles.ADMIN,
      mustNotBeViewer: true,
    },
  ],
  [RequestStepAction.UN_SKIP]: [
    {
      states: [RequestStepStateEnum.PENDING],
      types: skippableBlockTypes,
      role: Roles.ADMIN,
      mustNotBeViewer: true,
    },
    {
      states: [RequestStepStateEnum.PENDING],
      types: [BlockTypeEnum.NOTIFICATION],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
    },
    { states: [RequestStepResultEnum.ERROR], types: [BlockTypeEnum.HTTP], role: Roles.ADMIN, mustNotBeViewer: true },
  ],
  [RequestStepAction.ROLLBACK]: [
    {
      states: [RequestStepStateEnum.COMPLETE],
      types: rollbackableBlockTypes,
      omneanOnly: true,
      role: Roles.ADMIN,
      shouldOnlyApplyToMostRecentStage: false,
      mustNotBeViewer: true,
    },
    {
      states: [RequestStepStateEnum.COMPLETE],
      types: rollbackableBlockTypes,
      role: Roles.ADMIN,
      shouldOnlyApplyToMostRecentStage: false,
      mustNotBeViewer: true,
      needsConfigPermission: true,
    },
    {
      // P2P_ADMIN_MODE (frontend-only HubSpot stop-gap): grant rollback to admins without config
      // permission when the flag is on. featureFlaggedActions[ROLLBACK] mirrors the flag, and the
      // backend already permits the admin role to roll back.
      states: [RequestStepStateEnum.COMPLETE],
      types: rollbackableBlockTypes,
      role: Roles.ADMIN,
      shouldOnlyApplyToMostRecentStage: false,
      mustNotBeViewer: true,
      checkFeatureFlag: true,
    },
  ],
  [RequestStepAction.RERUN_ACCOUNTING_SYNC]: [
    {
      states: [RequestStepStateEnum.RUNNING],
      types: [BlockTypeEnum.ENGAGEMENT, BlockTypeEnum.COMMIT_SUPPLIER_DATA, BlockTypeEnum.CREATE_PO],
      role: Roles.ADMIN,
      requiresAccountingIntegration: true,
      requiresTaskErrors: true,
      requiresAllUserTasksComplete: true,
      requiresStepNotLoading: true,
      mustNotBeViewer: true,
    },
  ],
  [RequestStepAction.HTTP_RETRY]: [
    {
      states: [RequestStepResultEnum.ERROR, RequestStepStateEnum.RUNNING],
      types: [BlockTypeEnum.HTTP, BlockTypeEnum.EXTERNAL_OPERATION, BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
      requiresTaskErrors: true,
    },
  ],
  [RequestStepAction.DATA_TRANSFORMATION_RETRY]: [
    {
      states: [RequestStepResultEnum.ERROR],
      types: [BlockTypeEnum.DATA_TRANSFORMATION],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
    },
  ],
  [RequestStepAction.POLL]: [
    {
      states: [RequestStepStateEnum.RUNNING],
      types: [BlockTypeEnum.EXTERNAL_OPERATION],
      role: Roles.USER,
    },
    {
      states: [RequestStepStateEnum.RUNNING],
      types: [BlockTypeEnum.EXTERNAL_OPERATION],
      role: Roles.ADMIN,
    },
    {
      states: [RequestStepStateEnum.COMPLETE],
      types: [BlockTypeEnum.CREATE_PO],
      role: Roles.USER,
      requiresAccountingIntegration: true,
    },
    {
      states: [RequestStepStateEnum.COMPLETE],
      types: [BlockTypeEnum.CREATE_PO],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
      requiresAccountingIntegration: true,
    },
  ],
  [RequestStepAction.REFRESH_STATUS]: [
    {
      states: [RequestStepStateEnum.RUNNING, RequestStepStateEnum.COMPLETE],
      types: [BlockTypeEnum.EXTERNAL_OPERATION],
      role: Roles.USER,
    },
    {
      states: [RequestStepStateEnum.RUNNING, RequestStepStateEnum.COMPLETE],
      types: [BlockTypeEnum.EXTERNAL_OPERATION],
      role: Roles.ADMIN,
    },
  ],
  [RequestStepAction.SKIP_CONDITIONS]: [
    {
      states: [RequestStepStateEnum.PENDING],
      types: [
        BlockTypeEnum.APPROVAL,
        BlockTypeEnum.BUDGET_APPROVAL,
        BlockTypeEnum.ENGAGEMENT,
        BlockTypeEnum.HTTP,
        BlockTypeEnum.NOTIFICATION,
        BlockTypeEnum.RENEWAL_ASSESSMENT,
        BlockTypeEnum.SUPPLIER_ONBOARDING,
        BlockTypeEnum.EXTERNAL_OPERATION,
        BlockTypeEnum.CREATE_RENEWAL,
        BlockTypeEnum.COMMIT_SUPPLIER_DATA,
        BlockTypeEnum.CREATE_AGREEMENT,
        BlockTypeEnum.CANCEL_AGREEMENT,
        BlockTypeEnum.CREATE_PO,
        BlockTypeEnum.RFX,
        BlockTypeEnum.CREATE_ASSESSMENT,
        BlockTypeEnum.PRICE_BENCHMARK,
        BlockTypeEnum.DATA_TRANSFORMATION,
        BlockTypeEnum.WAITING,
        BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES,
        BlockTypeEnum.AI_FORM_COMPLETION,
      ],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
      needsConfigPermission: true,
      checkFeatureFlag: true,
      requiresConditionality: true,
    },
  ],
  [RequestStepAction.UPDATE_CONFIG]: [
    {
      states: [RequestStepStateEnum.PENDING],
      types: [BlockTypeEnum.EXTERNAL_OPERATION, BlockTypeEnum.CREATE_AGREEMENT],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
      needsConfigPermission: true,
      checkFeatureFlag: true,
    },
    {
      states: [RequestStepResultEnum.ERROR, RequestStepStateEnum.RUNNING],
      types: [BlockTypeEnum.EXTERNAL_OPERATION, BlockTypeEnum.CREATE_AGREEMENT],
      role: Roles.ADMIN,
      mustNotBeViewer: true,
      needsConfigPermission: true,
      checkFeatureFlag: true,
    },
  ],
}

const isValidAction = (action: string): action is RequestStepAction => {
  return (AllowedActions as RequestStepAction[]).includes(action as RequestStepAction)
}

type MatchPermissionSetProps = {
  rules: PermissionRule[]
  role: Roles
  type: BlockTypeEnum
  state: ActionStepStates
  isAssignee: boolean
  isOmnean: boolean
  hasConfigPermission: boolean
  isViewOnly: boolean
  isMostRecentStage: boolean
  hasAccountingIntegration: boolean
  hasTaskErrors: boolean
  areAllUserTasksComplete: boolean
  isStepLoading: boolean
  featureFlagEnabled: boolean
  isAssigneeManagementRestricted: boolean
  hasOpenAmendment: boolean
  requestIsInFlight: boolean
  hasConditionality: boolean
}

const matchPermissionSet = ({
  rules,
  role,
  type,
  state,
  isAssignee,
  isOmnean,
  hasConfigPermission,
  isViewOnly,
  isMostRecentStage,
  hasAccountingIntegration,
  hasTaskErrors,
  areAllUserTasksComplete,
  isStepLoading,
  featureFlagEnabled,
  isAssigneeManagementRestricted,
  hasOpenAmendment,
  requestIsInFlight,
  hasConditionality,
}: MatchPermissionSetProps) => {
  return rules.some((rule) => {
    return (
      rule.role === role &&
      rule.types.includes(type) &&
      rule.states.includes(state) &&
      (rule.omneanOnly ? isOmnean : true) &&
      (rule.needsConfigPermission ? hasConfigPermission : true) &&
      (rule.mustNotBeViewer ? !isViewOnly : true) &&
      (rule.mustBeAssignee ? isAssignee : true) &&
      (rule.shouldOnlyApplyToMostRecentStage ? isMostRecentStage : true) &&
      (rule.requiresAccountingIntegration ? hasAccountingIntegration : true) &&
      (rule.requiresTaskErrors ? hasTaskErrors : true) &&
      (rule.requiresAllUserTasksComplete ? areAllUserTasksComplete : true) &&
      (rule.requiresStepNotLoading ? !isStepLoading : true) &&
      (rule.checkFeatureFlag ? featureFlagEnabled : true) &&
      (rule.killedByAssigneeRestriction ? !isAssigneeManagementRestricted : true) &&
      (rule.requiresOpenAmendment ? hasOpenAmendment : true) &&
      (rule.requiresRequestInFlight ? requestIsInFlight : true) &&
      (rule.requiresConditionality ? hasConditionality : true)
    )
  })
}

export type FeatureFlaggedActions = Partial<Record<RequestStepAction, boolean>>

interface GetAllowedActionsProps {
  context: BlockContext
  isAssignee: boolean
  hasAccountingIntegration: boolean
  hasTaskErrors: boolean
  areAllUserTasksComplete: boolean
  isRequestPaused?: boolean
  isStepLoading: boolean
  featureFlaggedActions?: FeatureFlaggedActions
  restrictAssigneeManagement?: boolean
  hasOpenAmendment?: boolean
  requestIsInFlight?: boolean
  /** P2P_ADMIN_MODE: when on, admins without config permission lose force-approve/skip. Frontend-only HubSpot stop-gap. */
  p2pAdminMode?: boolean
}
export const getAllowedActions = ({
  context,
  isAssignee,
  hasAccountingIntegration,
  hasTaskErrors,
  areAllUserTasksComplete,
  isRequestPaused,
  isStepLoading,
  featureFlaggedActions = {},
  restrictAssigneeManagement,
  hasOpenAmendment = false,
  requestIsInFlight = false,
  p2pAdminMode = false,
}: GetAllowedActionsProps): RequestStepAction[] => {
  const { user, state, step, stepIsSkippedInFuture, type, isMostRecentStage, node } = context
  const isAssigneeManagementRestricted =
    !!restrictAssigneeManagement && user.role === Roles.USER && !user.hasConfigPermissions && !user.isViewOnly
  const hasConditionality = node?.metadata?.conditionConfig != null
  const result = step?.result ?? RequestStepResultEnum.PASS
  const actions: RequestStepAction[] = []

  // Special Case (consider converting to flag?)
  const actionState =
    result === RequestStepResultEnum.ERROR &&
    [
      BlockTypeEnum.HTTP,
      BlockTypeEnum.EXTERNAL_OPERATION,
      BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES,
      BlockTypeEnum.DATA_TRANSFORMATION,
    ].includes(type)
      ? result
      : state

  const filteredPermissions = Object.fromEntries(
    Object.entries(permissions).filter(
      ([action]) =>
        isValidAction(action) &&
        (stepIsSkippedInFuture ? action !== RequestStepAction.SKIP : action !== RequestStepAction.UN_SKIP) &&
        (isRequestPaused ? PausedActions.includes(action) : true)
    )
  )

  for (const [action, rules] of Object.entries(filteredPermissions)) {
    if (
      isValidAction(action) &&
      matchPermissionSet({
        rules,
        role: user.role,
        type,
        state: actionState,
        isAssignee,
        isOmnean: user.isOmnean,
        hasConfigPermission: user.hasConfigPermissions,
        // TODO SB(SR-504): Remove enforced isViewOnly
        isViewOnly: !!user.isViewOnly,
        isMostRecentStage,
        hasAccountingIntegration,
        hasTaskErrors,
        areAllUserTasksComplete,
        isStepLoading,
        featureFlagEnabled: featureFlaggedActions[action] ?? false,
        isAssigneeManagementRestricted,
        hasOpenAmendment,
        requestIsInFlight,
        hasConditionality,
      })
    ) {
      actions.push(action)
    }
  }

  // P2P_ADMIN_MODE (frontend-only HubSpot stop-gap): admins without config permission
  // cannot force-approve or skip request steps. Super admins (hasConfigPermissions) are unaffected.
  if (p2pAdminMode && !user.hasConfigPermissions) {
    return actions.filter((action) => action !== RequestStepAction.FORCE_APPROVE && action !== RequestStepAction.SKIP)
  }

  return actions
}
